import { useEffect, useState, useRef } from "react";
import axios from "axios";
import ProfileInfoDisplay from "./ProfileInfoDisplay";
import EditProfileForm from "./EditProfileForm";
import ChangePasswordForm from "./ChangePasswordForm";
import AdminList from "./AdminList";
import ActivityLogs from "./ActivityLogs";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const ProfileSettings = () => {
  const [admin, setAdmin] = useState(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [globalMessage, setGlobalMessage] = useState("");
  const [loadingError, setLoadingError] = useState(false);
  const fetchInProgress = useRef(false);

  const token = JSON.parse(localStorage.getItem("user"))?.token;

  const fetchAdminProfile = async () => {
    if (!token) {
      setLoadingError(true);
      setGlobalMessage("❌ Token missing. Please login.");
      return;
    }
    if (fetchInProgress.current) return; // skip if fetch in progress
    fetchInProgress.current = true;

    try {
      const res = await axios.get(`${API_BASE}/api/admin/profile/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdmin(res.data);
      setLoadingError(false);
      setGlobalMessage("");
    } catch (err) {
      console.error("Failed to load admin profile:", err);
      setLoadingError(true);
      setGlobalMessage("❌ Failed to load profile. Please try again.");
    } finally {
      fetchInProgress.current = false;
    }
  };

  useEffect(() => {
    fetchAdminProfile();
  }, [token, reloadTrigger]);

  if (loadingError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-red-600 dark:text-red-400 font-semibold">
          Unable to load profile.
        </p>
        <button
          onClick={() => {
            setReloadTrigger((t) => t + 1);
            setGlobalMessage("");
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!admin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-gray-400">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6 max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-4">
        Profile Settings
      </h2>

      {globalMessage && (
        <p className="text-sm mb-4 text-blue-600 dark:text-blue-400">{globalMessage}</p>
      )}

      {/* Profile Display */}
      <ProfileInfoDisplay admin={admin} />

      {/* Edit Profile */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
          Edit Profile
        </h3>
        <EditProfileForm
          admin={admin}
          onUpdate={() => setReloadTrigger((t) => t + 1)}
          onMessage={setGlobalMessage}
        />
      </div>

      {/* Change Password */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
          Change Password
        </h3>
        <ChangePasswordForm />
      </div>

      {/* Superadmin Sections */}
      {admin.isSuperadmin && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Admin Management
            </h3>
            <AdminList />
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Activity Logs
            </h3>
            <ActivityLogs />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSettings;
